import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import { useStravaModal } from "../contexts/StravaModalContext";
import { useNotifications } from "../contexts/NotificationContext";
import Sidebar from "./Sidebar";
import NotificationBell from "./NotificationBell";
import ProfilePicture from "./ProfilePicture";
import "./ProfileNew.css";

// Import icons
import iconFlameStreak from "../assets/icon-flame-streak.svg";
import iconTrophy from "../assets/icon-trophy-leaderboard.svg";
import iconMenu from "../assets/icon-menu.svg";
import iconSearch from "../assets/icon-search.svg";

const STRAVA_BACKFILL_KEY = "stravaBackfillLastRun";
const STRAVA_BACKFILL_THROTTLE_MINUTES = 30;
const WEATHER_BACKFILL_KEY = "weatherBackfillLastRun";
const WEATHER_BACKFILL_THROTTLE_MINUTES = 240;

const shouldRunTask = (key, minutes) => {
  const rawValue = localStorage.getItem(key);
  if (!rawValue) {
    return true;
  }
  const lastRun = Number(rawValue);
  if (Number.isNaN(lastRun)) {
    return true;
  }
  return Date.now() - lastRun > minutes * 60 * 1000;
};

const markTaskRun = (key) => {
  localStorage.setItem(key, Date.now().toString());
};

const NOTIFICATION_DEFAULTS = {
  weeklySummary: true,
  friendActivity: true,
  productUpdates: false
};

const ProfileNew = ({ user: propUser, onLogout }) => {
  const navigate = useNavigate();
  const { openModal: openStravaModal, registerStatusListener } = useStravaModal();
  const { refreshNotifications } = useNotifications();

  // Get user from props or localStorage
  const user = propUser && propUser.email
    ? propUser
    : JSON.parse(localStorage.getItem('user') || '{}');

  // Redirect if no user
  useEffect(() => {
    if (!user || !user.email) {
      navigate('/');
    }
  }, [user, navigate]);

  const [activeTab, setActiveTab] = useState("profile");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [password, setPassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [dataStatusMessage, setDataStatusMessage] = useState(null);
  const [dataStatusVariant, setDataStatusVariant] = useState("info");
  const { unit, convertDistance, setUnit } = useDistanceUnit();
  const [shareStats, setShareStats] = useState('friends');
  const [privacyConsentDate, setPrivacyConsentDate] = useState(null);
  const [personalCard, setPersonalCard] = useState(null);
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [cardText, setCardText] = useState("");
  const [cardColor, setCardColor] = useState("#ffffff");
  const [temperatureUnit, setTemperatureUnit] = useState(() => {
    return localStorage.getItem("temperatureUnit") || "c";
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    country: "",
    is_visible: true,
    profile_picture_url: null
  });
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
const [stats, setStats] = useState({
  current_streak: 0,
  total_runs: 0,
  total_distance: 0,
  badges_earned: 0
});
const [stravaConnected, setStravaConnected] = useState(false);
const [stravaAthlete, setStravaAthlete] = useState(null);
const [stravaSyncedActivities, setStravaSyncedActivities] = useState(0);
const [stravaLastSync, setStravaLastSync] = useState(null);
const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = registerStatusListener((payload) => {
      setStravaConnected(Boolean(payload?.connected));
      setStravaAthlete(payload?.athlete || null);
      setStravaSyncedActivities(payload?.syncedActivities || 0);
      setStravaLastSync(payload?.lastSync || null);
    });
    return unsubscribe;
  }, [registerStatusListener]);
  const [notificationPreferences, setNotificationPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem("notificationPreferences");
      if (!stored) {
        return NOTIFICATION_DEFAULTS;
      }
      const parsed = JSON.parse(stored);
      return {
        ...NOTIFICATION_DEFAULTS,
        ...parsed
      };
    } catch (error) {
      console.error("Error parsing notification preferences:", error);
      return NOTIFICATION_DEFAULTS;
    }
  });

  const triggerWeatherBackfill = useCallback(
    async ({ limit = 20 } = {}) => {
      if (!user?.email) {
        return;
      }
      if (!shouldRunTask(WEATHER_BACKFILL_KEY, WEATHER_BACKFILL_THROTTLE_MINUTES)) {
        return;
      }
      try {
        await apiClient.post('/weather/backfill', { limit });
        markTaskRun(WEATHER_BACKFILL_KEY);
        console.log("Weather backfill triggered automatically");
      } catch (error) {
        console.error("Weather backfill failed (non-critical):", error);
      }
    },
    [user.email]
  );

  const triggerStravaBackfill = useCallback(async () => {
    if (!user?.email) {
      return;
    }
    if (!shouldRunTask(STRAVA_BACKFILL_KEY, STRAVA_BACKFILL_THROTTLE_MINUTES)) {
      return;
    }
    try {
      await apiClient.post('/strava/sync/backfill', { days: 7 });
      markTaskRun(STRAVA_BACKFILL_KEY);
      console.log("Automatic Strava backfill triggered");
    } catch (error) {
      console.error("Backfill failed (non-critical):", error);
    }
  }, [user.email]);

  const fetchProfileData = useCallback(async () => {
    try {
      let response;

      try {
        response = await apiClient.get('/profile');
      } catch (error) {
        if (error.response?.status === 404) {
          response = await apiClient.get('/');
        } else {
          throw error;
        }
      }

      const profile = response.data.profile || {};
      setProfileData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        country: profile.country || "",
        is_visible: profile.is_visible !== false,
        profile_picture_url: profile.profile_picture_url || null
      });
    } catch (error) {
      console.error("Error fetching profile data:", error);
    }
  }, []);

  const fetchPrivacySettings = useCallback(async () => {
    try {
      const response = await apiClient.get('/privacy');
      setShareStats(response.data.share_stats);
      setPrivacyConsentDate(response.data.privacy_consent_date);
    } catch (error) {
      console.error("Error fetching privacy settings:", error);
    }
  }, []);

  const fetchPersonalCard = useCallback(async () => {
    try {
      const userObj = JSON.parse(localStorage.getItem("user") || "{}");
      if (userObj.id) {
        const response = await apiClient.get(`/friends/profile/${userObj.id}`);
        if (response.data.personal_card) {
          setPersonalCard(response.data.personal_card);
          setCardText(response.data.personal_card.card_text || "");
          setCardColor(response.data.personal_card.card_color || "#ffffff");
        }
      }
    } catch (error) {
      console.error("Error fetching personal card:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/activities/runstreak');
      setStats({
        current_streak: response.data.current_streak || 0,
        total_runs: response.data.total_runs || 0,
        total_distance: response.data.total_distance || 0,
        badges_earned: 0 // Placeholder for future implementation
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  const fetchStravaStatus = useCallback(async () => {
    if (!user?.email) {
      return;
    }
    try {
      const response = await apiClient.get('/strava/status');
      setStravaConnected(response.data.connected);
      setStravaAthlete(response.data.athlete);
      setStravaSyncedActivities(response.data.synced_activities || 0);
      setStravaLastSync(response.data.last_sync || null);

      // Trigger automatic backfill if Strava is connected
      if (response.data.connected) {
        triggerStravaBackfill();
        triggerWeatherBackfill();
      }
    } catch (error) {
      console.error("Error fetching Strava status:", error);
    }
  }, [triggerStravaBackfill, triggerWeatherBackfill, user.email]);

  useEffect(() => {
    const loadData = async () => {
      if (!user || !user.email) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        await Promise.all([
          fetchProfileData(),
          fetchPrivacySettings(),
          fetchPersonalCard(),
          fetchStats(),
          fetchStravaStatus()
        ]);
        refreshNotifications();
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user, fetchProfileData, fetchPrivacySettings, fetchPersonalCard, fetchStats, fetchStravaStatus, refreshNotifications]);

  useEffect(() => {
    localStorage.setItem(
      "notificationPreferences",
      JSON.stringify(notificationPreferences)
    );
  }, [notificationPreferences]);

  useEffect(() => {
    localStorage.setItem("temperatureUnit", temperatureUnit);
  }, [temperatureUnit]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const renderTopBar = () => (
    <div className="profile-topbar">
      <div className="profile-topbar__left">
        <button className="profile-topbar__icon-button" onClick={toggleSidebar}>
          <img src={iconMenu} alt="Menu" />
        </button>
        <h1 className="profile-topbar__title">Runstreak Dashboard</h1>
      </div>
      <div className="profile-topbar__right">
        <div className="profile-topbar__search">
          <img src={iconSearch} alt="Search" className="profile-topbar__search-icon" />
          <input
            type="text"
            placeholder="Search"
            className="profile-topbar__search-input"
          />
        </div>
        <NotificationBell />
        <button className="profile-topbar__logout" onClick={onLogout}>Logout</button>
        <ProfilePicture
          profilePictureUrl={profileData.profile_picture_url}
          firstName={profileData.first_name}
          lastName={profileData.last_name}
          email={user.email}
          size="small"
          showInitials={true}
        />
      </div>
    </div>
  );

  const updatePrivacySettings = async (newShareStats) => {
    try {
      const response = await apiClient.post('/privacy', {
        share_stats: newShareStats
      });

      setShareStats(response.data.share_stats);
      setPrivacyConsentDate(response.data.privacy_consent_date);

      const messages = {
        none: "Your stats are now private and only visible to you.",
        friends: "Your stats are now shared with friends only.",
        all: "Your stats are now visible to everyone."
      };
      alert(messages[newShareStats]);
    } catch (error) {
      console.error("Error updating privacy settings:", error);
      alert("Failed to update privacy settings. Please try again.");
    }
  };

  const handleNotificationToggle = (preferenceKey) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      [preferenceKey]: !prev[preferenceKey]
    }));
  };

  const handleDownloadData = () => {
    setDataStatusVariant("info");
    setDataStatusMessage("We will email you as soon as your activity archive is ready to download.");
  };

  const handleSignOutOtherSessions = () => {
    setDataStatusVariant("info");
    setDataStatusMessage("Session management is rolling out soon. We will notify you when sign-outs are complete.");
  };

  const handleRequestDataDeletion = () => {
    setDataStatusVariant("info");
    setDataStatusMessage("Your deletion request has been recorded. Our support team will follow up shortly.");
  };

  const handleTemperatureUnitChange = (value) => {
    setTemperatureUnit(value);
  };

  const savePersonalCard = async () => {
    try {
      const userObj = JSON.parse(localStorage.getItem("user") || "{}");
      if (!userObj.id) {
        throw new Error("User id missing in local storage");
      }

      await apiClient.post(`/friends/card/${userObj.id}`, {
        card_text: cardText,
        card_color: cardColor
      });

      setPersonalCard({ card_text: cardText, card_color: cardColor });
      setIsEditingCard(false);
      alert("Personal note saved successfully!");
    } catch (error) {
      console.error("Error saving personal card:", error);
      alert("Failed to save personal note. Please try again.");
    }
  };

  const saveProfileData = async () => {
    try {
      const payload = {
        ...profileData,
        first_name: profileData.first_name || "",
        last_name: profileData.last_name || "",
        country: profileData.country || "",
        is_visible: Boolean(profileData.is_visible)
      };

      try {
        await apiClient.post('/profile', payload);
      } catch (error) {
        if (error.response?.status === 404) {
          await apiClient.post('/', payload);
        } else {
          throw error;
        }
      }

      setIsEditingProfile(false);
      alert("Profile updated successfully!");
      await fetchProfileData();
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  const handleDeleteAccount = async () => {
    if (!password) {
      setDeleteError("Please enter your password");
      return;
    }

    try {
      await apiClient.post('/delete', {
        password: password
      });

      alert("Account deleted successfully");
      localStorage.clear();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      setDeleteError(error.response?.data?.message || "Failed to delete account. Please check your password.");
    }
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a PNG, JPEG, GIF, or WEBP image.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB limit.');
      return;
    }

    setProfilePictureFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadProfilePicture = async () => {
    if (!profilePictureFile) return;

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', profilePictureFile);

      const response = await apiClient.post(
        '/profile/picture',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      // Clear preview and file first
      setProfilePictureFile(null);
      setProfilePicturePreview(null);

      // Update profile data with new picture URL
      setProfileData(prev => ({
        ...prev,
        profile_picture_url: response.data.profile_picture_url
      }));

      // Force refetch profile to ensure we have the latest URL with fresh timestamp
      await fetchProfileData();

      alert('Profile picture uploaded successfully!');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert(error.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const deleteProfilePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) {
      return;
    }

    try {
      await apiClient.delete('/profile/picture');

      // Update profile data
      setProfileData(prev => ({
        ...prev,
        profile_picture_url: null
      }));

      // Clear preview and file
      setProfilePictureFile(null);
      setProfilePicturePreview(null);

      alert('Profile picture deleted successfully!');
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      alert(error.response?.data?.message || 'Failed to delete profile picture');
    }
  };

  const getDisplayName = () => {
    if (profileData.first_name && profileData.last_name) {
      return `${profileData.first_name} ${profileData.last_name}`;
    } else if (profileData.first_name) {
      return profileData.first_name;
    }
    return user?.email || 'User';
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={toggleSidebar}
          user={user}
          onLogout={onLogout}
        />
        <div className={`profile-main-container ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          {renderTopBar()}
          <div className="profile-content">
            <div className="profile-loading">Loading profile...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        user={user}
        onLogout={onLogout}
      />
      <div className={`profile-main-container ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
        {renderTopBar()}
        <div className="profile-content">
          {/* Header */}
          <div className="profile-header">
            <h1>Profile</h1>
          </div>

          {/* Tabs */}
          <div className="profile-tabs-container">
            <div className="profile-tabs">
              <button
                className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Profile
              </button>
              <button
                className={`profile-tab ${activeTab === 'achievements' ? 'active' : ''}`}
                onClick={() => setActiveTab('achievements')}
              >
                Achievements
              </button>
              <button
                className={`profile-tab ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                Settings
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'profile' && (
            <div className="profile-tab-content">
              {/* Profile Information Card */}
              <div className="profile-info-card">
                {!isEditingProfile ? (
                  <>
                    <div className="profile-info-header">
                      <div className="profile-info-left">
                        <ProfilePicture
                          profilePictureUrl={profileData.profile_picture_url}
                          firstName={profileData.first_name}
                          lastName={profileData.last_name}
                          email={user.email}
                          size="large"
                          className="profile-avatar"
                          showInitials={true}
                        />
                        <div className="profile-info-details">
                          <h2>{getDisplayName()}</h2>
                          <p>{user.email}</p>
                          {profileData.country && (
                            <div className="profile-info-meta">
                              <p>📍 {profileData.country}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        className="edit-profile-button"
                        onClick={() => setIsEditingProfile(true)}
                      >
                        ✏️ Edit
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="profile-edit-form">
                    {/* Profile Picture Upload */}
                    <div className="form-group">
                      <label>Profile Picture</label>
                      <div className="profile-picture-upload">
                        <div className="profile-picture-preview">
                          {profilePicturePreview ? (
                            <img src={profilePicturePreview} alt="Preview" className="profile-avatar profile-avatar-image" />
                          ) : (
                            <ProfilePicture
                              profilePictureUrl={profileData.profile_picture_url}
                              firstName={profileData.first_name}
                              lastName={profileData.last_name}
                              email={user.email}
                              size="large"
                              className="profile-avatar"
                              showInitials={true}
                            />
                          )}
                        </div>
                        <div className="profile-picture-actions">
                          <input
                            type="file"
                            id="profile-picture-input"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                            onChange={handleProfilePictureChange}
                            style={{ display: 'none' }}
                          />
                          <label htmlFor="profile-picture-input" className="btn-secondary">
                            Choose Picture
                          </label>
                          {profilePictureFile && (
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={uploadProfilePicture}
                              disabled={uploadingPicture}
                            >
                              {uploadingPicture ? 'Uploading...' : 'Upload'}
                            </button>
                          )}
                          {profileData.profile_picture_url && !profilePictureFile && (
                            <button
                              type="button"
                              className="btn-danger"
                              onClick={deleteProfilePicture}
                            >
                              Delete Picture
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="form-group-inline">
                      <div className="form-group">
                        <label>First Name (Optional)</label>
                        <input
                          type="text"
                          value={profileData.first_name || ""}
                          onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                          placeholder="Enter first name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Last Name (Optional)</label>
                        <input
                          type="text"
                          value={profileData.last_name || ""}
                          onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                          placeholder="Enter last name"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Country (Optional)</label>
                      <select
                        value={profileData.country || ""}
                        onChange={(e) => setProfileData({...profileData, country: e.target.value})}
                      >
                        <option value="">Select a country</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Canada">Canada</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Egypt">Egypt</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Germany">Germany</option>
                        <option value="Greece">Greece</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Italy">Italy</option>
                        <option value="Japan">Japan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Norway">Norway</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Russia">Russia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="Spain">Spain</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Turkey">Turkey</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United States">United States</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="checkbox-field">
                      <input
                        type="checkbox"
                        id="is-visible"
                        checked={profileData.is_visible}
                        onChange={(e) => setProfileData({...profileData, is_visible: e.target.checked})}
                      />
                      <label htmlFor="is-visible">
                        <strong>Allow others to find me in search</strong>
                        <p>When disabled, you won't appear in friend searches</p>
                      </label>
                    </div>

                    <div className="form-actions">
                      <button className="btn-primary" onClick={saveProfileData}>
                        Save Profile
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setIsEditingProfile(false);
                          fetchProfileData();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Grid */}
              <div className="profile-stats-grid">
                <div className="profile-stat-card">
                  <div className="stat-card-header">
                    <img src={iconFlameStreak} alt="" className="stat-card-icon" />
                    <span className="stat-card-label">Current Streak</span>
                  </div>
                  <div className="stat-card-value">{stats.current_streak}</div>
                  <p className="stat-card-subtitle">days</p>
                </div>

                <div className="profile-stat-card">
                  <div className="stat-card-header">
                    <img src={iconTrophy} alt="" className="stat-card-icon" />
                    <span className="stat-card-label">Total Runs</span>
                  </div>
                  <div className="stat-card-value">{stats.total_runs}</div>
                  <p className="stat-card-subtitle">activities</p>
                </div>

                <div className="profile-stat-card">
                  <div className="stat-card-header">
                    <span className="stat-card-label">Total {unit === 'km' ? 'KM' : 'Miles'}</span>
                  </div>
                  <div className="stat-card-value">
                    {Math.round(Number.parseFloat(convertDistance(stats.total_distance || 0)))}
                  </div>
                  <p className="stat-card-subtitle">{unit === 'km' ? 'kilometers' : 'miles'}</p>
                </div>

                <div className="profile-stat-card">
                  <div className="stat-card-header">
                    <span className="stat-card-label">Badges Earned</span>
                  </div>
                  <div className="stat-card-value">{stats.badges_earned}</div>
                  <p className="stat-card-subtitle">achievements</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="profile-tab-content">
              <div className="profile-empty-state">
                <h2>Achievements Coming Soon</h2>
                <p>Badge and achievement tracking will be available in a future update.</p>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="profile-tab-content">
              <div className="settings-grid">
                <div className="settings-section">
                  <h2>Units</h2>
                  <p>Choose how distance and temperature are displayed throughout the app.</p>
                  <div className="unit-groups">
                    <div className="unit-group">
                      <span className="unit-group-label">Distance</span>
                      <div className="unit-toggle">
                        <button
                          type="button"
                          className={unit === 'miles' ? 'active' : ''}
                          onClick={() => setUnit('miles')}
                        >
                          Miles
                        </button>
                        <button
                          type="button"
                          className={unit === 'km' ? 'active' : ''}
                          onClick={() => setUnit('km')}
                        >
                          Kilometers
                        </button>
                      </div>
                    </div>
                    <div className="unit-group">
                      <span className="unit-group-label">Temperature</span>
                      <div className="unit-toggle">
                        <button
                          type="button"
                          className={temperatureUnit === 'c' ? 'active' : ''}
                          onClick={() => handleTemperatureUnitChange('c')}
                        >
                          °C
                        </button>
                        <button
                          type="button"
                          className={temperatureUnit === 'f' ? 'active' : ''}
                          onClick={() => handleTemperatureUnitChange('f')}
                        >
                          °F
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <h2>Privacy & Data Sharing</h2>
                  <p>Control who can see your running statistics.</p>
                  <div className="privacy-options">
                    <label
                      className={`privacy-option ${shareStats === 'none' ? 'selected-none' : ''}`}
                    >
                      <input
                        type="radio"
                        name="shareStats"
                        value="none"
                        checked={shareStats === 'none'}
                        onChange={(e) => updatePrivacySettings(e.target.value)}
                      />
                      <div className="privacy-option-content">
                        <strong>Not sharing</strong>
                        <span>Your stats are completely private and only visible to you.</span>
                      </div>
                    </label>

                    <label
                      className={`privacy-option ${shareStats === 'friends' ? 'selected-friends' : ''}`}
                    >
                      <input
                        type="radio"
                        name="shareStats"
                        value="friends"
                        checked={shareStats === 'friends'}
                        onChange={(e) => updatePrivacySettings(e.target.value)}
                      />
                      <div className="privacy-option-content">
                        <strong>Share with friends</strong>
                        <span>Friends can see your stats in the leaderboard. This is the default.</span>
                      </div>
                    </label>

                    <label
                      className={`privacy-option ${shareStats === 'all' ? 'selected-all' : ''}`}
                    >
                      <input
                        type="radio"
                        name="shareStats"
                        value="all"
                        checked={shareStats === 'all'}
                        onChange={(e) => updatePrivacySettings(e.target.value)}
                      />
                      <div className="privacy-option-content">
                        <strong>Share with everyone</strong>
                        <span>Make your stats visible to the entire community.</span>
                      </div>
                    </label>
                  </div>

                  {privacyConsentDate && shareStats !== 'none' && (
                    <p className="privacy-consent-date">
                      Sharing enabled on: {new Date(privacyConsentDate).toLocaleDateString()}
                    </p>
                  )}

                  <div className="privacy-info">
                    Your current streak, longest streak, and total run days visibility depends on this setting.
                  </div>
                </div>

                <div className="settings-section">
                  <h2>Notification Preferences</h2>
                  <p>Decide which updates land in your inbox.</p>
                  <ul className="notification-list">
                    <li className="notification-item">
                      <div className="notification-copy">
                        <h3>Weekly training summary</h3>
                        <p>Recap of mileage, streaks, and badges every Monday morning.</p>
                      </div>
                      <button
                        type="button"
                        className={`toggle-switch ${notificationPreferences.weeklySummary ? 'active' : ''}`}
                        aria-pressed={notificationPreferences.weeklySummary}
                        onClick={() => handleNotificationToggle('weeklySummary')}
                      >
                        <span className="sr-only">Toggle weekly training summary emails</span>
                      </button>
                    </li>
                    <li className="notification-item">
                      <div className="notification-copy">
                        <h3>Friend activity alerts</h3>
                        <p>Get notified when friends hit new streak milestones.</p>
                      </div>
                      <button
                        type="button"
                        className={`toggle-switch ${notificationPreferences.friendActivity ? 'active' : ''}`}
                        aria-pressed={notificationPreferences.friendActivity}
                        onClick={() => handleNotificationToggle('friendActivity')}
                      >
                        <span className="sr-only">Toggle friend activity alerts</span>
                      </button>
                    </li>
                    <li className="notification-item">
                      <div className="notification-copy">
                        <h3>Product news & betas</h3>
                        <p>Hear about new features and early access programs.</p>
                      </div>
                      <button
                        type="button"
                        className={`toggle-switch ${notificationPreferences.productUpdates ? 'active' : ''}`}
                        aria-pressed={notificationPreferences.productUpdates}
                        onClick={() => handleNotificationToggle('productUpdates')}
                      >
                        <span className="sr-only">Toggle product update emails</span>
                      </button>
                    </li>
                  </ul>
                </div>

                <div className="settings-section personal-note-section">
                  <div className="personal-note-header">
                    <h2>My Personal Note</h2>
                    <button
                      className={isEditingCard ? "btn-secondary" : "btn-primary"}
                      onClick={() => setIsEditingCard(!isEditingCard)}
                    >
                      {isEditingCard ? 'Cancel' : 'Edit Note'}
                    </button>
                  </div>
                  <p style={{ color: '#717182', marginBottom: '16px', fontSize: '14px' }}>
                    This note appears on your profile when friends view your stats.
                  </p>

                  {isEditingCard ? (
                    <div className="profile-edit-form">
                      <div className="form-group">
                        <label>Note Text</label>
                        <textarea
                          value={cardText}
                          onChange={(e) => setCardText(e.target.value)}
                          placeholder="Add a personal note about yourself, your running goals, or motivation..."
                        />
                      </div>

                      <div className="form-group">
                        <label>Background Color</label>
                        <div className="color-picker-group">
                          <input
                            type="color"
                            value={cardColor}
                            onChange={(e) => setCardColor(e.target.value)}
                          />
                          <input
                            type="text"
                            value={cardColor}
                            onChange={(e) => setCardColor(e.target.value)}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>

                      <button className="btn-primary" onClick={savePersonalCard}>
                        Save Note
                      </button>
                    </div>
                  ) : (
                    <div
                      className="personal-note-preview"
                      style={{ background: personalCard?.card_color || '#f9fafb' }}
                    >
                      {personalCard?.card_text ? (
                        <p>{personalCard.card_text}</p>
                      ) : (
                        <p className="personal-note-empty">
                          No note added yet. Click "Edit Note" to add one.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="settings-section">
                  <h2>Connected Apps</h2>
                  <p>Manage integrations with your running tools.</p>
                  <div className={`integration-card ${stravaConnected ? 'connected' : 'disconnected'}`}>
                    <div className="integration-card-header">
                      <h3>Strava</h3>
                      <span className={`integration-status ${stravaConnected ? 'status-connected' : 'status-disconnected'}`}>
                        {stravaConnected ? 'Connected' : 'Not connected'}
                      </span>
                    </div>
                    <p>Sync activities automatically to keep your streaks up to date.</p>
                    {stravaConnected && stravaAthlete && (
                      <>
                        <p className="integration-meta">
                          Linked as {stravaAthlete.firstname} {stravaAthlete.lastname}
                        </p>
                        <p className="integration-meta">
                          {stravaSyncedActivities} {stravaSyncedActivities === 1 ? 'activity' : 'activities'} synced
                          {stravaLastSync && ` • Last sync: ${new Date(stravaLastSync).toLocaleDateString()}`}
                        </p>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn-primary integration-action"
                      onClick={() =>
                        openStravaModal({
                          tab: stravaConnected ? "sync" : "connection",
                        })
                      }
                    >
                      {stravaConnected ? "Manage connection" : "Connect Strava"}
                    </button>
                  </div>
                </div>

                <div className="settings-section">
                  <h2>Data & Security</h2>
                  <p>Export your data or manage active sessions.</p>
                  <div className="settings-actions">
                    <button type="button" className="btn-secondary" onClick={handleDownloadData}>
                      Download activity archive
                    </button>
                    <button type="button" className="btn-secondary" onClick={handleSignOutOtherSessions}>
                      Sign out of other sessions
                    </button>
                    <button type="button" className="btn-secondary" onClick={handleRequestDataDeletion}>
                      Request data deletion
                    </button>
                  </div>
                  {dataStatusMessage && (
                    <div className={`status-message status-${dataStatusVariant}`}>
                      {dataStatusMessage}
                    </div>
                  )}
                </div>

                <div className="settings-section danger-zone-section">
                  <h2>Danger Zone</h2>
                  <p>Once you delete your account, there is no going back. All your data will be permanently deleted.</p>

                  {!showDeleteConfirm ? (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete Account
                    </button>
                  ) : (
                    <div className="delete-confirm-box">
                      <p>Are you sure? This action cannot be undone.</p>
                      <div className="form-group">
                        <label htmlFor="delete-password">Enter your password to confirm:</label>
                        <input
                          type="password"
                          id="delete-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Your password"
                        />
                      </div>
                      {deleteError && (
                        <div className="delete-error">
                          {deleteError}
                        </div>
                      )}
                      <div className="form-actions">
                        <button className="btn-danger" onClick={handleDeleteAccount}>
                          Yes, Delete My Account
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setPassword("");
                            setDeleteError("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileNew;
